
import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Course, EnrolledCourse } from '@/types';
import { GraduationCap, Book, CheckCircle, PlayCircle, Award } from 'lucide-react';

interface LearningPathProps {
  pathTitle: string;
  pathDescription: string;
  courses: Course[];
  enrolledCourses: EnrolledCourse[];
  onContinue: (courseId: string, moduleId: string) => void;
  onEnroll: (courseId: string) => void;
}

const LearningPath: React.FC<LearningPathProps> = ({
  pathTitle,
  pathDescription,
  courses,
  enrolledCourses,
  onContinue,
  onEnroll
}) => {
  const enrolledCourseIds = enrolledCourses.map(ec => ec.courseId);
  const completedCourseIds = enrolledCourses
    .filter(ec => ec.progress === 100)
    .map(ec => ec.courseId);
  
  const pathProgress = courses.length > 0
    ? Math.round((completedCourseIds.length / courses.length) * 100)
    : 0;
  
  const totalXp = courses.reduce((sum, course) => sum + course.xpReward, 0);
  const earnedXp = courses
    .filter(course => completedCourseIds.includes(course.id))
    .reduce((sum, course) => sum + course.xpReward, 0);
  
  const getEnrolledCourse = (courseId: string) => 
    enrolledCourses.find(ec => ec.courseId === courseId);
  
  return (
    <Card>
      <CardHeader>
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-4">
          <div>
            <CardTitle className="text-xl">{pathTitle}</CardTitle>
            <p className="text-muted-foreground">{pathDescription}</p>
          </div>
          
          <div className="flex flex-col items-end">
            <Progress value={pathProgress} className="w-24 h-2 mb-1" />
            <div className="text-sm text-muted-foreground">
              {completedCourseIds.length}/{courses.length} courses
            </div>
            <Badge variant="outline" className="mt-2 flex items-center gap-1">
              <Award className="h-3 w-3" />
              <span>{earnedXp}/{totalXp} XP</span>
            </Badge>
          </div>
        </div>
      </CardHeader>
      
      <CardContent>
        <div className="space-y-4">
          {courses.map((course, index) => {
            const isEnrolled = enrolledCourseIds.includes(course.id);
            const isCompleted = completedCourseIds.includes(course.id);
            const enrolledCourse = getEnrolledCourse(course.id);
            
            return (
              <div 
                key={course.id} 
                className={`flex flex-col sm:flex-row items-start sm:items-center gap-4 p-4 rounded-lg border ${isCompleted ? 'bg-muted/30' : 'bg-card'}`}
              >
                <div className="flex items-center gap-4 flex-grow">
                  <div className="flex-shrink-0 w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                    {isCompleted ? (
                      <CheckCircle className="h-4 w-4 text-primary" />
                    ) : (
                      <span className="text-sm font-medium">{index + 1}</span>
                    )}
                  </div>
                  
                  <div className="min-w-0 flex-grow">
                    <h4 className="font-medium truncate">{course.title}</h4>
                    <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground mt-1">
                      <div className="flex items-center gap-1">
                        <Book className="h-3 w-3" />
                        <span>{course.duration} mins</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <GraduationCap className="h-3 w-3" />
                        <span>{course.level}</span>
                      </div>
                      <Badge variant="outline" className="flex items-center gap-1">
                        <Award className="h-3 w-3" />
                        <span>{course.xpReward} XP</span>
                      </Badge>
                    </div>
                  </div>
                </div>
                
                <div className="w-full sm:w-auto flex items-center gap-3">
                  {isEnrolled && !isCompleted && enrolledCourse && (
                    <div className="flex-grow sm:w-32">
                      <div className="flex justify-between text-xs mb-1">
                        <span>Progress</span>
                        <span>{enrolledCourse.progress}%</span>
                      </div>
                      <Progress value={enrolledCourse.progress} className="h-1.5" />
                    </div>
                  )}
                  
                  {isCompleted ? (
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => onContinue(course.id, enrolledCourse?.currentModuleId || '')}
                    >
                      Review
                    </Button>
                  ) : isEnrolled ? (
                    <Button 
                      size="sm"
                      onClick={() => onContinue(course.id, enrolledCourse?.currentModuleId || '')}
                      className="gap-1"
                    >
                      <PlayCircle className="h-3 w-3" />
                      <span>Continue</span>
                    </Button>
                  ) : (
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => onEnroll(course.id)}
                      className="gap-1"
                      disabled={index > 0 && !completedCourseIds.includes(courses[index - 1].id)}
                    >
                      <span>Start</span>
                    </Button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
};

export default LearningPath;
